// Single source of truth for the review-popup chip UI.
//
// Every review popup surface (touchpoint) resolves its title, subtitle, and
// chip list from here. To change what a popup shows, edit either
// DEFAULT_REVIEW_CHIPS (affects every touchpoint that does not override) or the
// specific REVIEW_CHIP_CONFIG[touchpoint] entry.

import { REVIEW_TRIGGERS } from "./reviewTriggers";

// One key per popup surface. Kept separate from REVIEW_TRIGGERS because a
// single touchpoint (e.g. PO) can be reached from more than one trigger.
export const REVIEW_TOUCHPOINTS = Object.freeze({
  FORECAST_REPORT: "FORECAST_REPORT",
  BEST_SELLERS: "BEST_SELLERS",
  SYNC: "SYNC",
  PO_FORECASTING: "PO_FORECASTING",
  EXPORT: "EXPORT",
  PO_INCOMING_INVENTORY: "PO_INCOMING_INVENTORY",
});

// Common starter chips. Edit this to change every popup that does not define
// its own chips list.
export const DEFAULT_REVIEW_CHIPS = Object.freeze([
  "Avoid a stockout",
  "Stop over-ordering",
  "Save time on manual work",
  "Simplify day-to-day restocking",
]);

// Shared subtitle shown under the title in every popup unless overridden.
export const REVIEW_CHIP_SUBTITLE = "Pick one — it takes 30 seconds to leave a review";

// Explicit entry for every touchpoint. Each can override title, subtitle, and
// chips independently; anything omitted falls back to the defaults above.
export const REVIEW_CHIP_CONFIG = Object.freeze({
  [REVIEW_TOUCHPOINTS.FORECAST_REPORT]: {
    title: "What does the Forecast Report help you with most?",
    chips: [
      "Avoid a stockout",
      "Stop over-ordering",
      "Save time on manual work",
      "Simplify day-to-day restocking",
    ],
  },
  [REVIEW_TOUCHPOINTS.BEST_SELLERS]: {
    title: "What does the Best Sellers report help you with most?",
    chips: [
      "Spot top products faster",
      "Prioritize what to reorder",
      "Save time on manual work",
      "Simplify day-to-day restocking",
    ],
  },
  [REVIEW_TOUCHPOINTS.SYNC]: {
    title: "What does inventory sync help you with most?",
    chips: [
      "Kept my incoming inventory updated automatically",
      "Saved time switching between apps",
      "Avoided Manually updating incoming stock in app",
    ],
  },
  [REVIEW_TOUCHPOINTS.PO_FORECASTING]: {
    title: "What does Purchase Order creation help you with most?",
    chips: [
      "Order the right quantities",
      "Stop over-ordering",
      "Save time on manual work",
      "Simplify day-to-day restocking",
    ],
  },
  [REVIEW_TOUCHPOINTS.EXPORT]: {
    title: "What does the export help you with most?",
    chips: [
      "Sent reorder list directly to my suppliers",
      "Replaced my manual spreadsheet tracking",
      "Saved time building my purchase order",
      "Shared reorder data with my team",
    ],
  },
  [REVIEW_TOUCHPOINTS.PO_INCOMING_INVENTORY]: {
    title: "What does incoming inventory help you with most?",
    chips: [
      "Account for stock in transit",
      "Avoid a stockout",
      "Save time on manual work",
      "Simplify day-to-day restocking",
    ],
  },
});

// Used only by the shared InAppReviewPrompt to map its current trigger to a
// touchpoint. Surfaces that already know their touchpoint (Export, Incoming
// Inventory) pass it directly and do not need this map.
export const TRIGGER_TO_TOUCHPOINT = Object.freeze({
  [REVIEW_TRIGGERS.FORECAST_REPORT_VIEWED]: REVIEW_TOUCHPOINTS.FORECAST_REPORT,
  [REVIEW_TRIGGERS.BEST_SELLERS_VIEWED]: REVIEW_TOUCHPOINTS.BEST_SELLERS,
  [REVIEW_TRIGGERS.SYNC_COMPLETED]: REVIEW_TOUCHPOINTS.SYNC,
  [REVIEW_TRIGGERS.PO_CREATED]: REVIEW_TOUCHPOINTS.PO_FORECASTING,
});

/*
 * Returns the resolved chip config for a touchpoint, filling any missing field
 * from the shared defaults.
 *
 * Example:
 *   getReviewChipConfig(REVIEW_TOUCHPOINTS.EXPORT)
 *   // => {
 *   //   title: "What does the export help you with most?",
 *   //   subtitle: "Pick one — it takes 30 seconds to leave a review",
 *   //   chips: ["Share forecasts with my team", ...]
 *   // }
 */
export const getReviewChipConfig = (touchpoint) => {
  const entry = REVIEW_CHIP_CONFIG[touchpoint] || {};
  return {
    title: entry.title || "What does this help you with most?",
    subtitle: entry.subtitle || REVIEW_CHIP_SUBTITLE,
    chips: entry.chips || DEFAULT_REVIEW_CHIPS,
  };
};
